import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '../../i18n/I18nContext';
import './FileUpload.css';

/**
 * FileUpload Component
 * 
 * A professional file upload component with drag & drop support,
 * preview (image/PDF), validation, and clear functionality.
 * 
 * @param {Object} props
 * @param {string} props.id - HTML id for the input
 * @param {string} props.label - Label text
 * @param {File|string} props.value - Controlled value (File object or URL string)
 * @param {Function} props.onChange - Handler called with (file)
 * @param {string} props.error - External error message
 * @param {string} props.className - Additional classes
 * @param {number} props.maxSizeMB - Max file size in MB (default 5)
 * @param {boolean} props.required - Whether input is required
 * @param {boolean} props.compact - Compact mode for tight spaces
 * @param {string} props.accept - Accepted file types (default: images & pdf)
 */
const FileUpload = ({
    id,
    label,
    value,
    onChange,
    error,
    className = '',
    maxSizeMB = 5,
    required = false,
    compact = false,
    accept = "image/png, image/jpeg, image/jpg, application/pdf"
}) => {
    const { t } = useI18n();
    const [preview, setPreview] = useState(null);
    const [fileType, setFileType] = useState(null); // 'image' or 'pdf'
    const [fileName, setFileName] = useState('');
    const [internalError, setInternalError] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const inputRef = useRef(null);

    // Analyze value (File or URL) to determine preview type
    useEffect(() => {
        if (value) {
            if (typeof value === 'string') {
                // It's a URL
                setFileName(value.split('/').pop());
                const ext = value.split('.').pop().toLowerCase();
                if (ext === 'pdf') {
                    setFileType('pdf');
                    setPreview(null);
                } else {
                    setFileType('image');
                    setPreview(value);
                }
            } else if (value instanceof File) {
                // It's a File object
                setFileName(value.name);
                if (value.type === 'application/pdf') {
                    setFileType('pdf');
                    setPreview(null);
                } else {
                    setFileType('image');
                    const objectUrl = URL.createObjectURL(value);
                    setPreview(objectUrl);
                    return () => URL.revokeObjectURL(objectUrl);
                }
            }
        } else {
            setPreview(null);
            setFileType(null);
            setFileName('');
        }
    }, [value]);

    const validateFile = (file) => {
        // Check file type
        const acceptedTypes = accept.split(',').map(t => t.trim());
        // Simple MIME check - complex pattern matching omitted for brevity
        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';

        // Allow if it matches generic 'image/*' or specific mime types
        const valid = acceptedTypes.some(type => {
            if (type.endsWith('/*')) return file.type.startsWith(type.slice(0, -2));
            return file.type === type;
        });

        if (!valid) {
            setInternalError(t('common.errorFileType') || 'Invalid file type');
            return false;
        }

        if (file.size > maxSizeMB * 1024 * 1024) {
            setInternalError(t('common.errorFileSize', { size: maxSizeMB }) || `File size exceeds ${maxSizeMB}MB`);
            return false;
        }
        setInternalError('');
        return true;
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (validateFile(file)) {
                onChange(file);
            } else {
                if (inputRef.current) inputRef.current.value = '';
            }
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!value) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            if (validateFile(file)) {
                onChange(file);
            }
        }
    };

    const handleClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(null);
        if (inputRef.current) inputRef.current.value = '';
        setInternalError('');
    };

    return (
        <div className={`file-upload-wrapper ${compact ? 'file-upload-compact' : ''} ${className}`}>
            {label && (
                <label htmlFor={id} className="field-label">
                    {label} {required && <span className="required">*</span>}
                </label>
            )}

            <div
                className={`file-upload-area ${isDragging ? 'dragging' : ''} ${error || internalError ? 'has-error' : ''} ${value ? 'has-file' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !value && inputRef.current?.click()} // Only click if empty, else use clear button
            >
                <input
                    type="file"
                    id={id}
                    ref={inputRef}
                    accept={accept}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                />

                {value ? (
                    <div className="file-preview-content">
                        {fileType === 'image' && preview ? (
                            <div className="preview-image-container">
                                <img src={preview} alt="Preview" className="file-image-preview" />
                            </div>
                        ) : (
                            <div className="preview-doc-container">
                                <i className={`bi ${fileType === 'pdf' ? 'bi-file-earmark-pdf text-danger' : 'bi-file-earmark-text text-primary'} doc-icon`}></i>
                            </div>
                        )}

                        <div className="file-info">
                            <span className="file-name" title={fileName}>{fileName}</span>
                            <span className="file-meta">
                                {fileType === 'image' ? 'Image' : 'Document'}
                                {value instanceof File && ` · ${(value.size / (1024 * 1024)).toFixed(1)} MB`}
                            </span>
                        </div>

                        <div className="file-actions">
                            <button
                                type="button"
                                className="btn-action-file btn-clear"
                                onClick={handleClear}
                                title="Remove file"
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="upload-placeholder">
                        {compact ? (
                            <div className="compact-placeholder">
                                <i className="bi bi-cloud-arrow-up"></i>
                                <span>{t('common.upload') || 'Upload'}</span>
                            </div>
                        ) : (
                            <>
                                <div className="upload-icon">
                                    <i className="bi bi-cloud-arrow-up"></i>
                                </div>
                                <div className="upload-text">
                                    <span className="primary-text">Click to upload</span> or drag and drop
                                </div>
                                <div className="upload-hint">
                                    PDF, JPG, PNG (max {maxSizeMB}MB)
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {(error || internalError) && (
                <div className="field-error-message">
                    {error || internalError}
                </div>
            )}
        </div>
    );
};

export default FileUpload;
